import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import { Card } from '@/components/ui';
import { useToast } from '@/components/Toast';
import { snoozeProblem, unsnoozeProblem } from '@/lib/api';
import type { StatusCheck, StatusItem, StatusLevel, StatusProblem, StatusView } from '@/lib/api';

// The Status card on Today (docs/hub-plan.md D3): a Homelab column with
// every machine, service, disk and share from any site (alexandria in
// Vancouver today, Home Assistant's box in Portland later; Jake,
// 2026-09-27: no column per site) and one for backups across all sites.
// Each Homelab row carries a site tag (PDX, VAN) for where the thing is:
// the check's own `site`, else its source's. Services are one row per
// site, merged across sources, that opens to a line per service (the Mac
// popover: StatusColumns.swift, SiteTag in Theme.swift).
// The Worker has already evaluated every check (status-core.ts); this
// only arranges and words it. Levels never rely on color alone: each dot
// sits next to a word.
//
// Alerts (D21): the Worker marks each problem for the viewer (snoozed,
// counted). A snoozed problem leaves its column's level and the pill and
// gets a moon, never grey (grey means not reporting); the pill counts
// only counted problems. Each problem line has a Snooze control, a native
// <select> styled as a button, so keyboards and phones need no menu.

const RANK: Record<StatusLevel, number> = { ok: 0, stale: 1, attention: 2, down: 3 };

function worst(levels: StatusLevel[]): StatusLevel {
  return levels.reduce<StatusLevel>((w, l) => (RANK[l] > RANK[w] ? l : w), 'ok');
}

const LEVEL_WORD: Record<StatusLevel, string> = {
  ok: 'All OK',
  stale: 'Not reporting',
  attention: 'Attention',
  down: 'Down',
};

interface Row {
  key: string;
  label: string;
  text: string;
  level: StatusLevel;
  reason: string | null;
  /** Its only problems are ones the viewer snoozed. */
  snoozed?: boolean;
  /** Homelab rows: where the thing is ("portland"), drawn as a PDX or VAN tag. */
  site?: string;
  /** A Services row: a line per service, shown when the row is opened. */
  sub?: Row[];
}

/** A check as buildColumns sees it: a snoozed problem is ok for its row and marked. */
type Marked = StatusCheck & { snoozed?: boolean };

interface Column {
  key: string;
  title: string;
  level: StatusLevel;
  rows: Row[];
  note: string | null;
  /** Snoozed problems in it (the head says "1 snoozed" when nothing else is wrong). */
  snoozed: number;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "today 6:21 PM", "yesterday 3:32 PM", "3 days ago" — in the viewer's time zone. */
export function whenBackedUp(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  if (localDay(t) === localDay(now)) return `today ${clock(iso)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (localDay(t) === localDay(yesterday)) return `yesterday ${clock(iso)}`;
  const days = Math.max(2, Math.round((now.getTime() - t.getTime()) / 86_400_000));
  return `${days} days ago`;
}

function backupText(c: StatusCheck): string {
  if (c.metrics?.running) {
    return typeof c.metrics.percent === 'number' ? `backing up · ${Math.round(c.metrics.percent)}%` : 'backing up';
  }
  if (c.metrics?.failed) return "didn't finish";
  if (c.metrics?.lastBackupAt) return whenBackedUp(c.metrics.lastBackupAt);
  return c.ok ? 'no backup yet' : "couldn't read";
}

function siteTitle(site: string): string {
  return site
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const SITE_CODE: Record<string, string> = { portland: 'PDX', vancouver: 'VAN' };

/** A site's tag: the airport-style code Jake uses (PDX, VAN), else its first three letters. */
function siteCode(site: string): string {
  return SITE_CODE[site] ?? site.slice(0, 3).toUpperCase();
}

function snoozedIn(list: Array<{ snoozed?: boolean }>): boolean {
  return list.length > 0 && list.every((c) => c.snoozed);
}

export function buildColumns(view: StatusView): Column[] {
  const snoozedKeys = new Set(view.problems.filter((p) => p.snoozed).map((p) => `${p.source}:${p.checkId ?? ''}`));
  const col: Column = { key: 'homelab', title: 'Homelab', level: 'ok', rows: [], note: null, snoozed: 0 };
  const levels: StatusLevel[] = [];
  const backupRows: Row[] = [];
  // Homelab's order: every source's machines, then disks without a machine, Services per site, shares.
  const machineRows: Row[] = [];
  const diskRows: Row[] = [];
  const mountRows: Row[] = [];
  // The UniFi network's summary and flags (portland-network), last.
  const networkRows: Row[] = [];
  // The collectors' composites (Plex), after the machines.
  const compositeRows: Row[] = [];
  // Services from every source, by where they are, sites in first-seen order.
  const servicesAt = new Map<string, Array<{ source: string; c: Marked }>>();
  for (const src0 of view.sources) {
    // A snoozed check counts as ok for its row and column; `snoozed` marks the row.
    const isSnoozed = (id: string) => snoozedKeys.has(`${src0.source}:${id}`);
    const src = {
      ...src0,
      checks: src0.checks.map((c) => (c.level !== 'ok' && isSnoozed(c.id) ? { ...c, level: 'ok' as const, snoozed: true } : c)),
    };
    const sourceSnoozed = snoozedKeys.has(`${src0.source}:`);
    const siteOf = (c: StatusCheck) => c.site ?? src.site;
    const all: Marked[] = src.checks;
    for (const b of all.filter((c) => c.group === 'backups')) {
      backupRows.push({ key: b.id, label: b.label, text: backupText(b), level: b.level, reason: b.reason, snoozed: b.snoozed });
    }
    // Media checks are the Media card's (MediaCard below).
    const checks = all.filter((c) => c.group !== 'backups' && c.group !== 'media');
    // A Mac that reports only backups adds rows to Backups and nothing to Homelab.
    if (checks.length === 0 && all.length > 0) continue;
    if (src.asleep) {
      // A Mac with its lid shut: what it last said still stands, and it isn't a problem.
    } else if (src.collectedAt === null) {
      col.note ??= `${src.label} hasn't reported yet`;
    } else if (src.stale) {
      col.note ??= `No report from ${src.label} since ${clock(src.collectedAt)}`;
    }
    // A composite (Plex: its server, the media share, alexandria, janenas; Jake, 2026-09-27) is one
    // row, worst part first, that opens to its parts. Its services and shares show only there; its
    // machines keep their own rows too, since other things need them.
    const byId = new Map(checks.map((c) => [c.id, c] as const));
    const diskOf = new Map(checks.filter((c) => c.group === 'storage').map((c) => [c.label.toLowerCase(), c] as const));
    const inComposite = new Set<string>();
    for (const g of src0.composites ?? []) {
      const parts: Row[] = [];
      for (const id of g.parts) {
        const c = byId.get(id);
        if (!c) continue;
        if (c.group === 'machines') {
          const disk = diskOf.get(c.label.toLowerCase());
          parts.push({
            key: c.id,
            label: c.label,
            text: c.level !== 'ok' || c.snoozed ? 'offline' : (disk?.text ?? 'online'),
            level: worst([c.level, disk?.level ?? 'ok']),
            reason: c.reason ?? disk?.reason ?? null,
            snoozed: snoozedIn([c, disk].filter((x): x is Marked => !!x && (x.snoozed || x.level !== 'ok'))),
          });
        } else if (c.group === 'storage') {
          parts.push({ key: c.id, label: c.label, text: c.text ?? '', level: c.level, reason: c.reason, snoozed: c.snoozed });
        } else if (c.group === 'services' || c.group === 'mounts') {
          inComposite.add(c.id);
          const ok = c.level === 'ok' && !c.snoozed;
          // A service may say more than up ("scanned today 4:14 AM", the Plex library).
          const text = c.group === 'services' ? (ok ? (c.text ?? 'up') : 'not answering') : ok ? 'mounted' : 'not mounted';
          parts.push({ key: c.id, label: c.label, text, level: c.level, reason: c.reason, snoozed: c.snoozed });
        }
      }
      if (parts.length === 0) continue;
      const level = worst(parts.map((p) => p.level));
      const bad = parts.filter((p) => p.level !== 'ok' || p.snoozed);
      const worstPart = bad.find((p) => p.level === level) ?? bad[0];
      compositeRows.push({
        key: `${src.source}:${g.id}`,
        label: g.label,
        text: worstPart ? (worstPart.level !== 'ok' && worstPart.reason ? worstPart.reason : `${worstPart.label} ${worstPart.text}`) : 'up',
        level,
        reason: bad.find((p) => !p.snoozed)?.reason ?? null,
        snoozed: snoozedIn(bad),
        site: g.site ?? src.site,
        sub: parts,
      });
    }
    // A machine row carries its own disk, when one is reported under the same name.
    const disks = new Map(
      checks.filter((c) => c.group === 'storage').map((c) => [c.label.toLowerCase(), c] as const),
    );
    for (const m of checks.filter((c) => c.group === 'machines')) {
      const disk = disks.get(m.label.toLowerCase());
      disks.delete(m.label.toLowerCase());
      machineRows.push({
        key: m.id,
        label: m.label,
        text: m.level !== 'ok' ? 'offline' : m.snoozed ? 'offline' : (disk?.text ?? 'online'),
        level: worst([m.level, disk?.level ?? 'ok']),
        reason: m.reason ?? disk?.reason ?? null,
        snoozed: snoozedIn([m, disk].filter((c): c is Marked => !!c && (c.snoozed || c.level !== 'ok'))),
        site: siteOf(m),
      });
    }
    for (const d of disks.values()) {
      diskRows.push({ key: d.id, label: d.label, text: d.text ?? '', level: d.level, reason: d.reason, snoozed: d.snoozed, site: siteOf(d) });
    }
    for (const c of checks.filter((c) => c.group === 'services' && !inComposite.has(c.id))) {
      const list = servicesAt.get(siteOf(c)) ?? [];
      list.push({ source: src.source, c });
      servicesAt.set(siteOf(c), list);
    }
    // One shares row per site its mounts are at (nearly always the source's own).
    const mountsAt = new Map<string, Marked[]>();
    for (const c of checks.filter((c) => c.group === 'mounts' && !inComposite.has(c.id))) {
      const list = mountsAt.get(siteOf(c)) ?? [];
      list.push(c);
      mountsAt.set(siteOf(c), list);
    }
    for (const [site, mounts] of mountsAt) {
      const bad = mounts.filter((c) => c.level !== 'ok' || c.snoozed);
      mountRows.push({
        key: site === src.site ? `${src.source}:mounts` : `${src.source}:mounts:${site}`,
        label: mounts.length === 1 ? mounts[0].label : 'Shares',
        text: bad.length > 0 ? 'not mounted' : 'mounted',
        level: worst(mounts.map((c) => c.level)),
        reason: bad.find((c) => !c.snoozed)?.reason ?? null,
        snoozed: snoozedIn(bad),
        site,
      });
    }
    for (const c of checks.filter((c) => c.group === 'network')) {
      networkRows.push({
        key: `${src.source}:${c.id}`,
        label: c.label,
        text: c.level !== 'ok' && c.reason ? c.reason : (c.text ?? ''),
        level: c.level,
        reason: c.reason,
        snoozed: c.snoozed,
        site: siteOf(c),
      });
    }
    col.snoozed += checks.filter((c) => c.snoozed).length + (sourceSnoozed ? 1 : 0);
    if (src.stale && !src.asleep && !sourceSnoozed) levels.push('stale');
  }
  const serviceRows = [...servicesAt].map(([site, list]): Row => {
    const services = list.map((s) => s.c);
    const bad = services.filter((c) => c.level !== 'ok' || c.snoozed);
    return {
      key: `services:${site}`,
      label: 'Services',
      text: bad.length > 0 ? `${bad.map((c) => c.label).join(', ')} down` : `${services.length} of ${services.length} up`,
      level: worst(services.map((c) => c.level)),
      reason: bad.find((c) => !c.snoozed)?.reason ?? null,
      snoozed: snoozedIn(bad),
      site,
      sub: list.map(({ source, c }) => ({
        key: `${source}:${c.id}`,
        label: c.label,
        text: c.level !== 'ok' || c.snoozed ? 'not answering' : (c.text ?? 'up'),
        level: c.level,
        reason: c.reason,
        snoozed: c.snoozed,
      })),
    };
  });
  col.rows = [...machineRows, ...diskRows, ...compositeRows, ...serviceRows, ...mountRows, ...networkRows];
  col.level = worst([...col.rows.map((r) => r.level), ...levels]);
  const cols = col.rows.length > 0 || col.note !== null ? [col] : [];
  if (backupRows.length > 0) {
    cols.push({
      key: 'backups',
      title: 'Backups',
      level: worst(backupRows.map((r) => r.level)),
      rows: backupRows,
      note: null,
      snoozed: backupRows.filter((r) => r.snoozed).length,
    });
  }
  return cols;
}

function Dot({ level }: { level: StatusLevel }) {
  return <span className={`status-dot is-${level}`} aria-hidden="true" />;
}

function Moon() {
  return <Icon name="moon" size={13} className="status-moon" />;
}

/** "PDX", "VAN": where a Homelab row's machine or services are (Mac: SiteTag in Theme.swift). */
function SiteTag({ site }: { site: string }) {
  const tone = site === 'portland' || site === 'vancouver' ? ` is-${site}` : '';
  return (
    <span className={`status-site${tone}`} title={siteTitle(site)}>
      <span aria-hidden="true">{siteCode(site)}</span>
      <span className="visually-hidden">{` in ${siteTitle(site)} `}</span>
    </span>
  );
}

function RowLabel({ row, children }: { row: Row; children?: ReactNode }) {
  return (
    <span className="status-row-label">
      {row.snoozed && <Moon />}
      {row.label}
      {row.site && <SiteTag site={row.site} />}
      {children}
    </span>
  );
}

/** A Services or composite (Plex) row: a button that opens a line per service or part under it. */
function ServicesRow({ row, open, onToggle, listId }: { row: Row; open: boolean; onToggle: () => void; listId: string }) {
  return (
    <>
      <button
        type="button"
        className={`status-row is-toggle is-${row.level}${row.snoozed ? ' is-snoozed' : ''}`}
        title={row.reason ?? undefined}
        aria-expanded={open}
        aria-controls={listId}
        onClick={onToggle}
      >
        <RowLabel row={row}>
          <Icon name="chevronRight" size={12} stroke={2.2} className={open ? 'status-chevron is-open' : 'status-chevron'} />
        </RowLabel>
        <span className="status-row-text">{row.text}</span>
      </button>
      <ul id={listId} className="status-sub" hidden={!open}>
        {row.sub?.map((s) => (
          <li key={s.key} className={`is-${s.level}${s.snoozed ? ' is-snoozed' : ''}`} title={s.reason ?? undefined}>
            <span className="status-sub-label">
              {s.snoozed && <Moon />}
              {s.label}
            </span>
            <span className="status-sub-text">{s.text}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "9 AM", "Tue 9 AM", "Oct 3" — how long a snooze lasts, in the viewer's time zone. */
export function untilText(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  const time = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: t.getMinutes() ? '2-digit' : undefined });
  if (localDay(t) === localDay(now)) return time;
  if (t.getTime() - now.getTime() < 6 * 86_400_000) return `${DAY_NAMES[t.getDay()]} ${time}`;
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function snoozedLine(p: StatusProblem): string {
  const until = p.snoozed?.until;
  return until ? `Snoozed until ${untilText(until)}` : `Snoozed until ${fixedWords(p).toLowerCase()}`;
}

/** "Until it backs up" — the fix a problem of this kind waits for (like the Mac's recovery titles). */
function fixedWords(p: StatusProblem): string {
  switch (p.group) {
    case 'backups':
      return 'It backs up';
    case 'services':
      return 'It answers';
    case 'machines':
      return "It's back online";
    case 'mounts':
      return "It's mounted";
    case 'network':
      return 'It clears';
    case null:
      return 'It reports again';
    default:
      return "It's fixed";
  }
}

interface SnoozeChoice {
  value: string;
  label: string;
  /** ISO 8601, or null for until it's fixed. */
  until: () => string | null;
}

/** The choices for a problem (D21 section 1): shorter ones for something down. */
export function snoozeChoices(p: StatusProblem, now: Date = new Date()): SnoozeChoice[] {
  const nine = new Date(now);
  nine.setHours(9, 0, 0, 0);
  const beforeNine = now.getTime() < nine.getTime();
  if (!beforeNine) nine.setDate(nine.getDate() + 1);
  const morning: SnoozeChoice = {
    value: 'morning',
    label: beforeNine ? 'Until 9 AM' : 'Until tomorrow morning',
    until: () => nine.toISOString(),
  };
  const inMs = (ms: number) => () => new Date(Date.now() + ms).toISOString();
  const fixed: SnoozeChoice = { value: 'fixed', label: `Until ${fixedWords(p).charAt(0).toLowerCase()}${fixedWords(p).slice(1)}`, until: () => null };
  if (p.level === 'attention') {
    return [
      morning,
      { value: '3d', label: 'For 3 days', until: inMs(3 * 86_400_000) },
      { value: 'week', label: 'For a week', until: inMs(7 * 86_400_000) },
      fixed,
    ];
  }
  return [{ value: '1h', label: 'For 1 hour', until: inMs(3_600_000) }, morning, fixed];
}

function daysText(hours: number): string {
  const days = Math.round(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
}

function SnoozeSelect({ householdId, problem, onChange }: { householdId: string; problem: StatusProblem; onChange?: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const choices = snoozeChoices(problem);
  async function pick(value: string) {
    if (value === 'rules') return navigate('/settings/alerts#backups');
    if (value === 'settings') return navigate('/settings/alerts');
    const choice = choices.find((c) => c.value === value);
    if (!choice) return;
    setBusy(true);
    try {
      const until = choice.until();
      await snoozeProblem(householdId, problem, until);
      toast(until ? `Snoozed until ${untilText(until)}` : `Snoozed until ${fixedWords(problem).toLowerCase()}`);
      onChange?.();
    } catch (err) {
      toast(`Couldn't snooze: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="status-snooze">
      <select
        value=""
        disabled={busy}
        aria-label={`Snooze ${problem.label}`}
        onChange={(e) => void pick(e.target.value)}
      >
        <option value="" disabled hidden>
          Snooze
        </option>
        {problem.group === 'backups' && problem.limitHours && (
          <option disabled>
            {problem.label}: flagged after {daysText(problem.limitHours)}
          </option>
        )}
        <optgroup label="Snooze">
          {choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Settings">
          {problem.group === 'backups' && problem.limitHours && (
            <option value="rules">Change the {daysText(problem.limitHours)} limit…</option>
          )}
          <option value="settings">Alert settings…</option>
        </optgroup>
      </select>
    </span>
  );
}

function UnsnoozeButton({ householdId, problem, onChange }: { householdId: string; problem: StatusProblem; onChange?: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="status-snooze-btn"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await unsnoozeProblem(householdId, problem);
          onChange?.();
        } catch (err) {
          toast(`Couldn't unsnooze: ${(err as Error).message}`);
        } finally {
          setBusy(false);
        }
      }}
    >
      Unsnooze
    </button>
  );
}

export function StatusCard({
  view,
  householdId,
  onChange,
}: {
  view: StatusView;
  /** With it, each problem line gets Snooze / Unsnooze. */
  householdId?: string;
  /** After a snooze or unsnooze: reload the view. */
  onChange?: () => void;
}) {
  const columns = buildColumns(view);
  // Opened Services rows, by row key, for as long as the page is open.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const idBase = useId();
  const marked = view.mine !== undefined;
  // Same wording as the Mac popover's headline: a silent site isn't a problem count.
  const counted = marked ? view.problems.filter((p) => p.counted) : view.problems;
  const real = counted.filter((p) => p.level !== 'stale');
  const snoozedCount = view.mine?.snoozed ?? 0;
  const pillLevel: StatusLevel = marked ? view.mine!.level : view.level === 'none' ? 'ok' : view.level;
  const down = real.filter((p) => p.level === 'down').length;
  const attention = real.length - down;
  const attentionText = `${attention} need${attention === 1 ? 's' : ''} attention`;
  const onlySnoozed = real.length === 0 && pillLevel !== 'stale' && snoozedCount > 0;
  const quiet = marked && real.length === 0 && view.problems.some((p) => !p.snoozed && !p.counted && p.level !== 'stale');
  const pillText =
    real.length === 0
      ? pillLevel === 'stale'
        ? 'Not reporting'
        : onlySnoozed
          ? `${snoozedCount} snoozed`
          : quiet
            ? 'Nothing needs you'
            : 'All OK'
      : down && attention
        ? `${down} down · ${attentionText}`
        : down
          ? `${down} down`
          : attentionText;
  const latest = view.sources
    .map((s) => s.collectedAt)
    .filter((t): t is string => t !== null)
    .sort()
    .pop();
  // Problems not snoozed, the ones that count for the viewer first; then the snoozed ones, which never disappear.
  const open = view.problems.filter((p) => !p.snoozed);
  const listed = marked ? [...open.filter((p) => p.counted), ...open.filter((p) => !p.counted)] : open;
  const snoozed = view.problems.filter((p) => p.snoozed);
  return (
    <section className="card status-card" aria-labelledby="status-title">
      <div className="status-head">
        <h2 id="status-title" className="card-title">
          Status
        </h2>
        <span className={`status-pill ${onlySnoozed ? 'is-snoozed' : `is-${pillLevel}`}`}>
          {onlySnoozed ? <Moon /> : <Dot level={pillLevel} />}
          {pillText}
        </span>
        {latest && (
          <span className="status-checked">
            Checked {clock(latest)}
            {snoozedCount > 0 && !onlySnoozed ? ` · ${snoozedCount} snoozed` : ''}
          </span>
        )}
        {householdId && (
          <Link to="/settings/alerts" className="icon-btn status-settings" aria-label="Alert settings" title="Alert settings">
            <Icon name="settings" size={16} />
          </Link>
        )}
      </div>
      <div className="status-grid">
        {columns.map((col) => (
          <div key={col.key} className="status-col">
            <div className="status-col-head">
              <span className="status-col-title">{col.title}</span>
              {col.level === 'ok' && col.snoozed > 0 ? (
                <span className="status-col-level is-snoozed">
                  <Moon />
                  {col.snoozed} snoozed
                </span>
              ) : (
                <span className={`status-col-level is-${col.level}`}>
                  <Dot level={col.level} />
                  {LEVEL_WORD[col.level]}
                </span>
              )}
            </div>
            {col.rows.map((r) =>
              r.sub ? (
                <ServicesRow
                  key={r.key}
                  row={r}
                  open={opened.has(r.key)}
                  onToggle={() => toggle(r.key)}
                  listId={`${idBase}-${r.key}`}
                />
              ) : (
                <div
                  key={r.key}
                  className={`status-row is-${r.level}${r.snoozed ? ' is-snoozed' : ''}`}
                  title={r.reason ?? undefined}
                >
                  <RowLabel row={r} />
                  <span className="status-row-text">{r.text}</span>
                </div>
              ),
            )}
            {col.note && <p className="status-note">{col.note}</p>}
          </div>
        ))}
      </div>
      {(listed.length > 0 || snoozed.length > 0) && (
        <ul className="status-problems">
          {listed.map((p) => (
            <li key={`${p.source}:${p.checkId ?? 'source'}`} className={`is-${p.level}${marked && !p.counted ? ' is-quiet' : ''}`}>
              <Dot level={p.level} />
              <span className="status-problem-text">{p.reason}</span>
              {householdId && marked && <SnoozeSelect householdId={householdId} problem={p} onChange={onChange} />}
            </li>
          ))}
          {snoozed.map((p) => (
            <li key={`${p.source}:${p.checkId ?? 'source'}`} className="is-snoozed">
              <Moon />
              <span className="status-problem-text">
                {p.label} · {snoozedLine(p)}
              </span>
              {householdId && <UnsnoozeButton householdId={householdId} problem={p} onChange={onChange} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Media (Jake, 2026-09-27) ────────────────────────────────────────
//
// What SABnzbd is downloading and what Sonarr and Radarr added, from
// alexandria's collector (checks media.downloads and media.added). New
// titles each get a line; replacements (the downgrade campaign, language
// re-grabs) are one line. The Worker decides every level, a stuck import's
// row included (status-core evaluateMedia); problems also sit in the
// Status card's list with Snooze. The Mac popover's Media section is the
// same (StatusColumns.swift, mediaSection).

/** "26 GB", "1.4 TB", "830 MB" (decimal, as SABnzbd and the Mac show sizes). */
export function mediaBytes(n: number): string {
  for (const [unit, size] of [['TB', 1e12], ['GB', 1e9], ['MB', 1e6]] as const) {
    if (n >= size) {
      const v = n / size;
      return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${unit}`;
    }
  }
  return `${Math.round(n / 1e3)} KB`;
}

/** "40 min", "3 h 10 min", "2 days". */
export function timeLeft(seconds: number): string {
  const mins = Math.max(1, Math.round(seconds / 60));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) return mins % 60 && h < 10 ? `${h} h ${mins % 60} min` : `${h} h`;
  return `${Math.round(h / 24)} days`;
}

/** When a title was added: "8:25 PM" today, "yesterday", "Fri", "Sep 20". */
export function addedWhen(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  if (localDay(t) === localDay(now)) return clock(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (localDay(t) === localDay(yesterday)) return 'yesterday';
  if (now.getTime() - t.getTime() < 6 * 86_400_000) return DAY_NAMES[t.getDay()];
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function itemText(i: StatusItem): string {
  switch (i.state) {
    case 'downloading':
      return [typeof i.percent === 'number' ? `${Math.round(i.percent)}%` : 'downloading', i.detail].filter(Boolean).join(' · ');
    case 'queued':
      return 'waiting';
    case 'paused':
      return 'paused';
    case 'processing':
      return [i.detail ?? 'processing', i.forSeconds && i.forSeconds >= 60 ? timeLeft(i.forSeconds) : null].filter(Boolean).join(' · ');
    case 'blocked':
      return "can't import";
    case 'added':
      return i.at ? addedWhen(i.at) : '';
  }
}

interface MediaChecks {
  downloads: Marked | null;
  added: Marked | null;
  stale: boolean;
}

/** The first source's media checks, a snoozed problem shown as ok and marked (as in buildColumns). */
export function mediaChecks(view: StatusView): MediaChecks | null {
  const snoozedKeys = new Set(view.problems.filter((p) => p.snoozed).map((p) => `${p.source}:${p.checkId ?? ''}`));
  for (const src of view.sources) {
    const mark = (c: StatusCheck | undefined): Marked | null => {
      if (!c) return null;
      if (c.level !== 'ok' && snoozedKeys.has(`${src.source}:${c.id}`)) {
        return { ...c, level: 'ok', snoozed: true, items: c.items?.map((i) => ({ ...i, level: 'ok' as const })) };
      }
      return c;
    };
    const media = src.checks.filter((c) => c.group === 'media');
    if (media.length === 0) continue;
    return {
      downloads: mark(media.find((c) => c.id === 'media.downloads')),
      added: mark(media.find((c) => c.id === 'media.added')),
      stale: src.stale && !src.asleep,
    };
  }
  return null;
}

function MediaLine({ item }: { item: StatusItem }) {
  const level = item.level ?? 'ok';
  return (
    <li className={`is-${level}`} title={item.state === 'blocked' ? item.detail : undefined}>
      <span className="status-sub-label media-title">{item.title}</span>
      <span className="status-sub-text">{itemText(item)}</span>
      {item.state === 'downloading' && typeof item.percent === 'number' && (
        <span className="media-bar" aria-hidden="true">
          <span style={{ width: `${Math.max(2, Math.min(100, item.percent))}%` }} />
        </span>
      )}
      {item.state === 'blocked' && level !== 'ok' && item.detail && <span className="media-why">{item.detail}</span>}
    </li>
  );
}

export function MediaCard({ view }: { view: StatusView | null | undefined }) {
  const m = view ? mediaChecks(view) : null;
  if (!m) return null;
  const d = m.downloads;
  const level = m.stale ? 'stale' : worst([d?.level ?? 'ok', m.added?.level ?? 'ok']);
  const items = d?.items ?? [];
  // A stuck import carries its own problem; paused, stalled or unreadable is the queue's row.
  const headLevel = m.stale ? 'stale' : items.some((i) => (i.level ?? 'ok') !== 'ok') ? 'ok' : (d?.level ?? 'ok');
  const dm = d?.metrics;
  const replacements = dm?.replacements ?? 0;
  return (
    <Card className="media-card">
      <div className="card-head">
        <h2 className="card-title">Media</h2>
        {level !== 'ok' && (
          <span className={`status-col-level is-${level}`}>
            <Dot level={level} />
            {LEVEL_WORD[level]}
          </span>
        )}
      </div>
      {d && (
        <>
          <div
            className={`status-row is-${headLevel}${d.snoozed ? ' is-snoozed' : ''}`}
            title={headLevel === 'ok' ? undefined : (d.reason ?? undefined)}
          >
            <span className="status-row-label">
              {d.snoozed && <Moon />}
              Downloads
            </span>
            <span className="status-row-text">{d.text ?? ''}</span>
          </div>
          {(items.length > 0 || replacements > 0 || (dm?.waitingToProcess ?? 0) > 1) && (
            <ul className="status-sub media-list">
              {items.map((i, k) => (
                <MediaLine key={`${i.state}:${i.title}:${k}`} item={i} />
              ))}
              {(dm?.waitingToProcess ?? 0) > 1 && (
                <li>
                  <span className="status-sub-label">Waiting to unpack</span>
                  <span className="status-sub-text">{dm!.waitingToProcess}</span>
                </li>
              )}
              {replacements > 0 && (
                <li>
                  <span className="status-sub-label">
                    {replacements === 1 ? '1 replacement' : `${replacements} replacements`}
                  </span>
                  <span className="status-sub-text">
                    {[
                      dm?.replacementBytesLeft ? `${mediaBytes(dm.replacementBytesLeft)} left` : null,
                      dm?.replacementsBlocked ? `${dm.replacementsBlocked} can't import` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </li>
              )}
            </ul>
          )}
        </>
      )}
      {m.added && (
        <>
          <div className={`status-row is-${m.stale ? 'stale' : m.added.level}`} title={m.added.reason ?? undefined}>
            <span className="status-row-label">Added</span>
            <span className="status-row-text">{(m.added.items ?? []).length === 0 ? (m.added.text ?? 'nothing yet') : ''}</span>
          </div>
          {(m.added.items ?? []).length > 0 && (
            <ul className="status-sub media-list">
              {m.added.items!.map((i, k) => (
                <MediaLine key={`${i.title}:${k}`} item={i} />
              ))}
            </ul>
          )}
        </>
      )}
      {m.stale && <p className="status-note">What alexandria last reported.</p>}
    </Card>
  );
}
